import React, { useContext, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiCalendar, BiFilterAlt, BiX } from "react-icons/bi";
import { BsFilter } from "react-icons/bs";
import { toast } from "react-toastify";
import Select, { components } from "react-select";
import { RegulationContext } from "../../../../App";
import ReportingPeriod from "../../../../components/Regulation/ReportingPeriod";
import { CategoryList, CompareCategoryType, DataViewCategoryType, LS_AUTHORITY_KEY, LS_DV_FILTER_KEY, MonthsExt, StandardCategory, startYear } from "../../../../core/constants/common";
import CompanyService from "../../../../core/service/CompanyService";
import FormService from "../../../../core/service/FormService";
import KPIService from "../../../../core/service/KPIService";
import StandardService from "../../../../core/service/StandardService";
import Utils from "../../../../core/utilities/Utils";
import './DataViewFilterNew.scss';
import moment from "moment";

const MAX_VISIBLE_CHIPS = 2;

const flattenSelectOptions = (options) => {
	return (options ?? []).reduce((acc, item) => {
		if (item?.options) {
			return acc.concat(flattenSelectOptions(item.options));
		}
		acc.push(item);
		return acc;
	}, []);
};

const MultiSelectOption = (props) => {
	const { isSelected, label } = props;
	return (
		<components.Option {...props}>
			<Form.Check
				type="checkbox"
				readOnly
				checked={isSelected}
				label={label}
				className="dvSelect-option-check" />
		</components.Option>
	);
};

const MultiValueChip = (props) => {
	const { index, getValue } = props;
	if (index < MAX_VISIBLE_CHIPS) {
		return <components.MultiValue {...props} />;
	}
	if (index === MAX_VISIBLE_CHIPS) {
		return (
			<div className="dvSelect__multi-value dvSelect__multi-value--overflow">
				<span className="dvSelect__multi-value__label">{props.selectProps.moreLabel(getValue().length - MAX_VISIBLE_CHIPS)}</span>
			</div>
		);
	}
	return null;
};

const MultiSelectMenuList = (props) => {
	const { children, options, selectProps } = props;
	return (
		<components.MenuList {...props}>
			<div className="dvSelect__menu-actions">
				{
					!selectProps.hasSelectionLimit &&
					<button type="button"
						className="dvSelect__menu-action"
						onMouseDown={(e) => { e.preventDefault(); selectProps.onChange(flattenSelectOptions(options), { action: 'select-option' }); }}>
						{selectProps.selectAllLabel}
					</button>
				}
				<button type="button"
					className="dvSelect__menu-action dvSelect__menu-action--clear"
					onMouseDown={(e) => { e.preventDefault(); selectProps.onChange([], { action: 'clear' }); }}>
					{selectProps.clearAllLabel}
				</button>
			</div>
			{children}
		</components.MenuList>
	);
};

const DataViewFilterNew = ({ t, view, onApplyFilter, yearFilter, dataViewType, onResetFilter,
	viewFilter, forGraph = false, onLoad, onFilterHide, changeFilter,
	topicData, parentEntData, childEntData }) => {

	const [viewType, setViewType] = useState(view ?? 'CONSOLIDATED_VIEW');
	const dvFilter = viewType == 'COMPARE' ? [] : JSON.parse(localStorage.getItem(LS_DV_FILTER_KEY));
	const { glbRegulation } = useContext(RegulationContext);
	const glbRegulationId = glbRegulation?.id ?? "";
	const month = Utils.getMonthFromFrequency(glbRegulation.frequency);
	const year = new Date().getFullYear();
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;

	const [filter, setFilter] = useState({
		reportingPeriod: yearFilter ? `${yearFilter}-01-01` : dvFilter?.reportingPeriod ?? `${year}-${month}-01`,
		categoryType: dvFilter?.categoryType ?? DataViewCategoryType.ENVIRONMENTAL,
		topicIds: dvFilter?.topicIds ?? [],
		metricIds: dvFilter?.metricIds ?? [],
		businessUnitIds: dvFilter?.businessUnitIds ?? [],
		parentEntities: dvFilter?.parentEntities ?? [],
		childEntities: dvFilter?.childEntities ?? [],
		periodFrom: dvFilter?.periodFrom ?? `${year}-01-01`,
		periodTo: dvFilter?.periodTo != null ? dvFilter?.periodTo : dvFilter?.periodType == 'MONTHLY' ? null : `${year}-12-31`,
		groupIds: dvFilter?.groupIds ?? [],
		periodType: dvFilter?.periodType ?? 'ANNUAL',
		period: dvFilter?.period ?? null,
		showQuantitative: dvFilter?.showQuantitative ?? true,
		showQualitative: dvFilter?.showQualitative ?? false
	});
	const [topics, setTopics] = useState([]);
	const [parentEntities, setParentEntities] = useState([]);
	const [childEntities, setChildEntities] = useState([]);
	const [reportingFrequency, setReportingFrequency] = useState('ANNUAL');
	const [groups, setGroups] = useState([]);
	const [period, setPeriod] = useState([]);
	const [displayFilter, setDisplayFilter] = useState(true);
	const optionsMonth = MonthsExt({ t });
	const currentYear = new Date().getFullYear();
	const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
	const [selectedData, setSelectedData] = useState([]);
	const [goals, setGoals] = useState([]);
	const [entities, setEntities] = useState([]);
	const [kpis, setKPIs] = useState([]);
	const [kpiCategories, setKPICategories] = useState([]);
	const [users, setUsers] = useState([]);

	useEffect(() => {
		if (!glbRegulationId) return;
		getTopics();
		getEnterprisesByLevel(1);
		getEnterprisesByLevel(2);
		getGroups();
	}, [glbRegulationId, filter.categoryType, filter.showQualitative, dataViewType]);

	useEffect(() => {
		setReportingFrequency(filter.periodType);
	}, [filter.periodType]);

	useEffect(() => {
		setViewType(view);
	}, [view]);

	useEffect(() => {
		if (view !== 'COMPARE') {
			setFilter(changeFilter)
		}
	}, [changeFilter]);


	const getTopics = () => {
		const standardCategory = filter.showQualitative ? StandardCategory.QUALITATIVE : StandardCategory.QUANTITATIVE;
		StandardService.getTopicsByStandard(glbRegulationId, false, standardCategory, filter.categoryType, filter.showQualitative)
			.then(res => {
				if (res.status !== 200) return;
				setTopics(res.data);
				topicData?.(res.data);
			}).catch(err => { });
	};

	const getEnterprisesByLevel = (level) => {
		CompanyService.getEnterprisesByLevel(level).then(res => {
			if (res.status !== 200) return;

			const entities = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
			if (level === 1) {
				setParentEntities(entities);
				parentEntData(entities)
			} else if (level === 2) {
				setChildEntities(entities);
				childEntData(entities)
			}
		}).catch(err => { });
	};

	const getGroups = () => {
		FormService.listGroups().then(res => {
			if (res.status !== 200) return;

			setGroups(res.data);
		}).catch(err => { });
	};

	const onTopicsChange = (selectedItems) => {
		setFilter({
			...filter,
			topicIds: (selectedItems ?? []).map(item => item.id),
			metricIds: [],
		});
	};

	const getSelectedTopics = () => {
		const data = [];
		topics?.forEach(topic => {
			if (filter.topicIds.indexOf(topic.id) < 0) return;

			data.push(topic);
		});
		return data;
	};

	const onParentEntitiesChange = (selectedItems) => {
		setFilter({
			...filter,
			parentEntities: (selectedItems ?? []).map(item => item.id)
		});
	};

	const onChildEntitiesChange = (selectedItems) => {
		setFilter({
			...filter,
			childEntities: (selectedItems ?? []).map(item => item.id)
		});
	};

	const onGroupsChange = (selectedItems) => {
		setFilter({
			...filter,
			groupIds: (selectedItems ?? []).map(item => item.id)
		});
	};

	const getSelectedParentEntities = () => {
		const data = [];
		parentEntities?.forEach(p => {
			if (filter.parentEntities.indexOf(p.id) < 0) return;

			data.push(p);
		});
		return data;
	};

	const getSelectedChildEntities = () => {
		const data = [];
		childEntities?.forEach(c => {
			if (filter.childEntities.indexOf(c.id) < 0) return;

			data.push(c);
		});
		return data;
	};

	const getSelectedGroups = () => {
		const data = [];
		groups?.forEach(g => {
			if (filter.groupIds.indexOf(g.id) < 0) return;

			data.push(g);
		});
		return data;
	};

	// Groups child entities under their parent business unit when a parent link is available;
	// falls back to a flat list when the data has no resolvable parent (or only one group would result).
	const getChildEntityOptions = () => {
		if (!childEntities?.length || !parentEntities?.length) return childEntities;

		const groupsByLabel = new Map();
		const labelOrder = [];
		childEntities.forEach(child => {
			const parentId = child.parentId ?? child.parentBusinessUnitId ?? child.parent?.id ?? null;
			const parent = parentId != null ? parentEntities.find(p => p.id === parentId) : null;
			const groupLabel = parent?.name ?? t("Others.label");
			if (!groupsByLabel.has(groupLabel)) {
				groupsByLabel.set(groupLabel, []);
				labelOrder.push(groupLabel);
			}
			groupsByLabel.get(groupLabel).push(child);
		});

		if (labelOrder.length <= 1) return childEntities;
		return labelOrder.map(label => ({ label, options: groupsByLabel.get(label) }));
	};

	const buildQuantitativePeriodDefaults = () => {
		const pFrom = `${year}-01-01`;
		const pTo = `${year}-12-31`;
		return {
			periodType: 'ANNUAL',
			periodFrom: pFrom,
			periodTo: pTo,
			reportingPeriod: pFrom,
		};
	};

	const buildQualitativePeriodDefaults = () => {
		const currentMonth = new Date().getMonth() + 1;
		const paddedMonth = String(currentMonth).padStart(2, '0');
		const pFrom = `${year}-${paddedMonth}-01`;
		const endingDay = Utils.getLastDayFor(pFrom);
		const pTo = `${year}-${paddedMonth}-${endingDay}`;
		return {
			periodType: 'MONTHLY',
			periodFrom: pFrom,
			periodTo: pTo,
			reportingPeriod: pFrom,
		};
	};

	const normalizeFilterForApply = (filterData) => {
		if (filterData.periodFrom) {
			if (filterData.showQuantitative || filterData.showQualitative) {
				filterData.reportingPeriod = filterData.periodFrom;
			}
		}
		return filterData;
	};

	const applyFilter = () => {
		const filterData = normalizeFilterForApply(JSON.parse(JSON.stringify(filter)));
		filterData.businessUnitIds = [];
		filterData.parentEntities?.forEach(e => {
			filterData.businessUnitIds.push(e)
		});
		filterData.childEntities?.forEach(e => {
			filterData.businessUnitIds.push(e)
		});

		if (filterData.periodType === 'CUSTOM') {
			const fromDate = moment(filterData.periodFrom);
			const toDate = moment(filterData.periodTo);
			if (toDate.isSame(fromDate, 'month')) {
				toast.warning('Same period range is not valid', {
					position: toast.POSITION.TOP_RIGHT,
					autoClose: 2000
				});
			} else if (toDate.isAfter(fromDate)) {
				onApplyFilter(filterData);
				onFilterHide(false);
			} else {
				toast.warning('Invalid period range', {
					position: toast.POSITION.TOP_RIGHT,
					autoClose: 2000
				});
			}
		} else {
			onApplyFilter(filterData);
			onFilterHide(false);
		}
	};
	const resetFilter = () => {
		setFilter({
			reportingPeriod: yearFilter ? `${yearFilter}-01-01` : `${new Date().getFullYear()}-01-01`,
			categoryType: DataViewCategoryType.ENVIRONMENTAL,
			topicIds: [],
			metricIds: [],
			businessUnitIds: [],
			parentEntities: [],
			childEntities: [],
			periodFrom: yearFilter ? `${yearFilter}-01-01` : `${new Date().getFullYear()}-01-01`,
			periodTo: yearFilter ? `${yearFilter}-12-31` : `${new Date().getFullYear()}-12-31`,
			groupIds: [],
			periodType: 'ANNUAL',
			period: null,
			showQuantitative: true,
			showQualitative: false
		});
		setSelectedData([])
		localStorage.removeItem(LS_DV_FILTER_KEY);
		const filterToSend = {
			reportingPeriod: yearFilter ? `${yearFilter}-01-01` : `${new Date().getFullYear()}-01-01`,
			categoryType: DataViewCategoryType.ENVIRONMENTAL,
			topicIds: [],
			metricIds: [],
			businessUnitIds: [],
			parentEntities: [],
			childEntities: [],
			periodFrom: yearFilter ? `${yearFilter}-01-01` : `${new Date().getFullYear()}-01-01`,
			periodTo: yearFilter ? `${yearFilter}-12-31` : `${new Date().getFullYear()}-12-31`,
			groupIds: [],
			periodType: 'ANNUAL',
			period: null,
			showQuantitative: true,
			showQualitative: false
		}
		onApplyFilter(filterToSend);
	};

	const renderReportingPeriodFilter = () => {
		if (viewType === 'EDIT_VIEW') {
			return (
				<div>
					<div className="row m-0">
						{/* className={`p-2 ${reportingFrequency === 'MONTHLY' ? 'col-lg-12' : 'col-lg-12'}`} */}
						<div className="col-lg-6 p-2">
							<label className="mb-1">{t("Type.label")}</label>
							<div className="DataType_box002">
								<Form.Check
									type="radio"
									inline
									label={t("Quantitative.label")}
									className="DataTypeCheck"
									name="typeck"
									id={`pageCheckbox-1`}
									checked={filter.showQuantitative}
									onChange={() => {
										const f = {
											...filter,
											showQuantitative: true,
											showQualitative: false,
											...buildQuantitativePeriodDefaults(),
										};
										setFilter(f);
										setReportingFrequency('ANNUAL');
									}}
								/>
								<Form.Check
									type="radio"
									inline
									name="typeck"
									id={`pageCheckbox-2`}
									label={t("Qualitative.label")}
									className="DataTypeCheck"
									checked={filter.showQualitative}
									onChange={() => {
										const f = {
											...filter,
											showQualitative: true,
											showQuantitative: false,
											...buildQualitativePeriodDefaults(),
										};
										setFilter(f);
										setReportingFrequency('MONTHLY');
									}}
								/>
							</div>
						</div>

						<div className='col-lg-6 p-2'>
							<label className="mb-1">{t("Frequency.label")}</label>
							<div className="period_chooseBox">
								<Form.Check inline label={t("Monthly.label")}
									className="period_checkbox"
									type="radio" id="freqMonthRadio" value={'MONTHLY'}
									checked={reportingFrequency === 'MONTHLY' ? "checked" : ""}
									onChange={(e) => { onReportingMonthlyFrequencyChange('MONTHLY') }}
								/>
							</div>
						</div>
						<div className="row">
							{/* className={`p-2 ${reportingFrequency === 'MONTHLY' ? 'col-lg-12' : 'col-lg-12'}`} */}
							<div className='col-lg-6 p-2'>
								<label className="mb-1">Year</label>
								<ReportingPeriod
									useFyYearLabels
									defaultFrequency={reportingFrequency}
									defaultPeriod={filter.periodTo ? filter.periodTo : filter.reportingPeriod}
									valueChangeCallback={(period) => setPeriodFilterForMonthlyView({ from: period })} />
							</div>
							{/* {
								(reportingFrequency === 'MONTHLY' && filter.showQuantitative) &&
								<div className="col-md-6 py-2 ">
									<ReportingPeriod
										defaultFrequency={reportingFrequency}
										defaultPeriod={viewType === 'CONSOLIDATED_VIEW' ? filter.periodTo : filter.reportingPeriod}
										valueChangeCallback={(period) => setPeriodFilterForConsolidatedView({ to: period })} />
								</div>
							} */}
						</div>
					</div>
					{/* <div className="row">
						<div className="col-lg-6 p-2">
							<label>{t("ReportingPeriod.label")}</label>
							<ReportingPeriod
								defaultPeriod={filter.reportingPeriod}
								valueChangeCallback={(period) =>
									setFilter({ ...filter, reportingPeriod: `${period.year}-${period.month}-01` })
								} />
						</div>
					</div> */}
				</div>
			);
		}
		if (viewType == 'CONSOLIDATED_VIEW') {
			return (
				<div className="row m-0">
					{/* className={`p-2 ${reportingFrequency === 'MONTHLY' ? 'col-lg-12' : 'col-lg-12'}`} */}
					<div className="col-lg-6 p-2 pb-1">
						<label className="mb-1">{t("Type.label")}</label>
						<div className="DataType_box002">
							<Form.Check
								type="radio"
								inline
								label="Quantitative"
								className="DataTypeCheck"
								name="typeck"
								id={`pageCheckbox-1`}
								checked={filter.showQuantitative}
								onChange={() => {
									const f = {
										...filter,
										showQuantitative: true,
										showQualitative: false,
										topicIds: [],
										metricIds: [],
										...buildQuantitativePeriodDefaults(),
									};
									setFilter(f);
									setReportingFrequency('ANNUAL');
								}}
							/>
							<Form.Check
								type="radio"
								inline
								name="typeck"
								id={`pageCheckbox-2`}
								label="Qualitative"
								className="DataTypeCheck"
								checked={filter.showQualitative}
								onChange={() => {
									const f = {
										...filter,
										showQualitative: true,
										showQuantitative: false,
										topicIds: [],
										metricIds: [],
										...buildQualitativePeriodDefaults(),
									};
									setFilter(f);
									setReportingFrequency('MONTHLY');
								}}
							/>
						</div>
					</div>

					<div className='col-lg-6 p-2 pb-1'>
						<label className="mb-1">{t("Frequency.label")}</label>
						<div className="period_chooseBox">
							{
								filter.showQuantitative &&
								<>
									<Form.Check inline label={t("Yearly.label")}
										className="period_checkbox"
										type="radio" id="freqYearRadio" value={'ANNUAL'}
										checked={reportingFrequency === 'ANNUAL' ? "checked" : ""}
										onChange={(e) => onReportingFrequencyChange('ANNUAL')} />
									<Form.Check inline label={t("Quarterly.label")}
										className="period_checkbox"
										type="radio" id="freqQuarterRadio" value={'QUARTERLY'}
										checked={reportingFrequency === 'QUARTERLY' ? "checked" : ""}
										onChange={(e) => onReportingFrequencyChange('QUARTERLY')} />
									<Form.Check inline label={t("Monthly.label")}
										className="period_checkbox"
										type="radio" id="freqMonthRadio" value={'MONTHLY'}
										checked={reportingFrequency === 'MONTHLY' ? "checked" : ""}
										onChange={(e) => onReportingFrequencyChange('MONTHLY')} />
									<Form.Check inline label={t("Custom.label")}
										className="period_checkbox"
										type="radio" id="freqCustomRadio" value={'CUSTOM'}
										checked={reportingFrequency === 'CUSTOM' ? "checked" : ""}
										onChange={(e) => onReportingFrequencyChange('CUSTOM')} />
								</>
							}
							{
								filter.showQualitative &&
								<Form.Check inline label={t("Monthly.label")}
									className="period_checkbox"
									type="radio" id="freqMonthRadio" value={'MONTHLY'}
									checked={reportingFrequency === 'MONTHLY' ? "checked" : ""}
									onChange={(e) => { onReportingMonthlyFrequencyChange('MONTHLY') }} />
							}
						</div>
					</div>


					{/* className={`p-2 ${reportingFrequency === 'MONTHLY' ? 'col-lg-12' : 'col-lg-12'}`} */}
					{
						filter.showQuantitative &&
						<div className='col-lg-6 p-2 pb-1'>
							<label className="mb-1">{filter.periodType === 'CUSTOM' ? 'Period From' : t("Period.label")}</label>
							<ReportingPeriod
								useFyYearLabels
								defaultFrequency={reportingFrequency}
								defaultPeriod={filter.periodFrom ? filter.periodFrom : filter.reportingPeriod}
								valueChangeCallback={(period) => { setPeriodFilterForConsolidatedView({ from: period }) }} />
						</div>}
					{
						(reportingFrequency === 'CUSTOM' && filter.showQuantitative) &&
						<div className="col-md-6 py-2 pb-1">
							<label className="mb-1">{filter.periodType === 'CUSTOM' ? 'Period To' : t("Period.label")}</label>
							<ReportingPeriod
								useFyYearLabels
								defaultFrequency={reportingFrequency}
								defaultPeriod={filter.periodTo ? filter.periodTo : filter.reportingPeriod}
								valueChangeCallback={(period) => { setPeriodFilterForConsolidatedView({ to: period }) }} />
						</div>
					}
					{
						(filter.showQualitative) &&
						<div className="col-md-6 py-2 pb-1">
							<label className="mb-1">{t("Year.label")}</label>
							<ReportingPeriod
								useFyYearLabels
								defaultFrequency={reportingFrequency}
								defaultPeriod={filter.periodFrom ? filter.periodFrom : filter.reportingPeriod}
								valueChangeCallback={(period) => { setPeriodFilterForMonthlyView({ from: period }) }} />
						</div>
					}
				</div>
			);
		}
	};

	const setPeriodFilterForConsolidatedView = ({ from, to }) => {
		let pFrom = null;
		let pTo = null;
		if (reportingFrequency === 'QUARTERLY') {
			let qMonthStart, qMonthEnd;
			if (from.month === '01') {
				qMonthStart = `01`; qMonthEnd = `03`;
			} else if (from.month === '04') {
				qMonthStart = '04'; qMonthEnd = `06`;
			} else if (from.month === '07') {
				qMonthStart = '07'; qMonthEnd = `09`;
			} else {
				qMonthStart = '10'; qMonthEnd = `12`;
			}
			pFrom = `${from.year}-${qMonthStart}-01`;
			let endingDay = Utils.getLastDayFor(`${from.year}-${qMonthEnd}-01`);
			pTo = `${from.year}-${qMonthEnd}-${endingDay}`;
		} else if (reportingFrequency === 'ANNUAL') {
			// Other frequencies, the from will have to be set
			pFrom = `${from.year}-01-01`;
			pTo = `${from.year}-12-31`;
		} else if (reportingFrequency === 'CUSTOM') {
			// Custom
			if (from) {
				pFrom = `${from.year}-${from.month}-01`;
				pTo = filter.periodFrom;
			} else {
				pFrom = filter.periodFrom;
				let endingDay = Utils.getLastDayFor(`${to.year}-${to.month}-01`);
				pTo = `${to.year}-${to.month}-${endingDay}`;
			}
		} else {
			// Monthly
			pFrom = `${from.year}-${from.month}-01`;
			pTo = null;
		}
		setFilter({
			...filter,
			periodFrom: pFrom,
			periodTo: pTo,
			reportingPeriod: pFrom
		});
	};

	const setPeriodFilterForMonthlyView = ({ from, to }) => {
		let pFrom = null;
		let pTo = null;
		if (from) {
			pFrom = `${from.year}-${from.month}-01`;
			let endingDay = Utils.getLastDayFor(`${from.year}-${from.month}-01`);
			pTo = `${from.year}-${from.month}-${endingDay}`;
		} else {
			let endingDay = Utils.getLastDayFor(`${from.year}-${from.month}-01`);
			pTo = null;
		}
		setFilter({
			...filter,
			periodFrom: pFrom,
			periodTo: pTo,
			period: null,
			reportingPeriod: pFrom
		});
	};

	const onReportingFrequencyChange = (freq) => {
		setReportingFrequency(freq);
		const dateParts = filter.periodFrom.split('-');
		const yearPart = dateParts[0] ?? year;
		const monthPart = dateParts[1] ?? month;
		let pFrom = `${yearPart}-01-01`;;
		let pTo = `${yearPart}-12-31`;;
		switch (freq) {
			case 'QUARTERLY': {
				pFrom = `${yearPart}-01-01`;
				let endingDay = Utils.getLastDayFor(`${yearPart}-03-01`);
				pTo = `${yearPart}-03-${endingDay}`;
				break;
			}
			case 'MONTHLY': {
				pFrom = `${yearPart}-01-01`;
				let endingDay = Utils.getLastDayFor(`${yearPart}-01-01`);
				pTo = null;
				break;
			}
			case 'CUSTOM': {
				pFrom = `${yearPart}-${monthPart}-01`;
				let endingDay = Utils.getLastDayFor(`${yearPart}-${monthPart}-01`);
				pTo = `${yearPart}-${monthPart}-${endingDay}`;
				break;
			}
			default:
				break;
		}
		setFilter({
			...filter,
			periodFrom: pFrom,
			periodTo: pTo,
			periodType: freq
		});
	};

	const onReportingMonthlyFrequencyChange = (freq) => {
		setReportingFrequency(freq);
		const dateParts = filter.periodFrom.split('-');
		const yearPart = dateParts[0] ?? year;
		let pFrom = `${yearPart}-01-01`;;
		let pTo = `${yearPart}-12-31`;;
		switch (freq) {
			case 'QUARTERLY': {
				pFrom = `${yearPart}-01-01`;
				let endingDay = Utils.getLastDayFor(`${yearPart}-03-01`);
				pTo = `${yearPart}-03-${endingDay}`;
				break;
			}
			case 'MONTHLY': {
				pFrom = `${yearPart}-${new Date().getMonth() + 1}-01`;
				let endingDay = Utils.getLastDayFor(`${yearPart}-${new Date().getMonth() + 1}-01`);
				pTo = null;
				break;
			}
			default:
				break;
		}
		setFilter({
			...filter,
			periodFrom: pFrom,
			periodTo: pTo,
			periodType: freq,
			reportingPeriod: pFrom
		});
	};

	const renderMetricRelatedFilters = () => {
		const compareLimit = viewType == 'COMPARE' ? 5 : null;
		const commonSelectComponents = { Option: MultiSelectOption, MultiValue: MultiValueChip, MenuList: MultiSelectMenuList };
		const commonSelectProps = {
			classNamePrefix: "dvSelect",
			unstyled: true,
			isMulti: true,
			placeholder: t("Select.label"),
			noOptionsMessage: () => t("NoData.label"),
			selectAllLabel: t("SelectAll.label"),
			clearAllLabel: t("ClearAll.label"),
			moreLabel: (count) => t("MoreSelected.label", { count }),
			components: commonSelectComponents,
			hideSelectedOptions: false,
			closeMenuOnSelect: false,
			menuPlacement: "top",
			menuPosition: "fixed",
		};
		return (
			<div className="row m-0">
				<div className="col-lg-6 p-2 pb-1">
					<label htmlFor="esgFilter">{t("Category.label")}</label>
					<select id="esgFilter"
						className="form-select"
						onChange={(e) => setFilter({ ...filter, categoryType: +e.target.value, topicIds: [], metricIds: [] })}
						required
						value={filter.categoryType}>
						{
							CategoryList({ t }).map(c => {
								return <option value={c.id} key={c.key}>{c.text}</option>
							})
						}
					</select>
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("Topic.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="topicMultiSelect"
						options={topics}
						value={getSelectedTopics()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onTopicsChange}
						hasSelectionLimit={!!compareLimit}
						isOptionDisabled={(o) => !!compareLimit && filter.topicIds.length >= compareLimit && filter.topicIds.indexOf(o.id) < 0} />
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("ParentEntity.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="parentEntityMultiselect"
						options={parentEntities}
						value={getSelectedParentEntities()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onParentEntitiesChange}
						hasSelectionLimit={!!compareLimit}
						isOptionDisabled={(o) => !!compareLimit && filter.parentEntities.length >= compareLimit && filter.parentEntities.indexOf(o.id) < 0} />
				</div>
				<div className="col-lg-6 p-2 pb-1">
					<label>{t("ChildEntity.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="childEntityMultiselect"
						options={getChildEntityOptions()}
						value={getSelectedChildEntities()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.name}
						onChange={onChildEntitiesChange}
						hasSelectionLimit={!!compareLimit}
						isOptionDisabled={(o) => !!compareLimit && filter.childEntities.length >= compareLimit && filter.childEntities.indexOf(o.id) < 0} />
				</div>

				<div className="col-lg-6 p-2 pb-1">
					<label>{t("EnterpriseGroup.label")}</label>
					<Select
						{...commonSelectProps}
						inputId="egMultiselect"
						options={groups}
						value={getSelectedGroups()}
						getOptionValue={(o) => o.id}
						getOptionLabel={(o) => o.groupName}
						onChange={onGroupsChange} />
				</div>
			</div>
		);
	};

	return (
		<div>
			{
				displayFilter &&
				<div className="dv-filter-holder 3">
					<div className="dv-filter-holder_head">
						<h5><BsFilter className="dv-filter-holder_headIcon" />{t("Filter.label")}</h5>
						<div className="btnClose" onClick={() => onFilterHide(false)}>
							<BiX />
						</div>
					</div>
					<div className="dv-filter-holder_body">
						<div className="dv-filter-section">
							{renderReportingPeriodFilter()}
						</div>
						{
							(dataViewType === 'ESG_REPORTING' || viewType === 'COMPARE') &&
							<div className="dv-filter-section">
								{renderMetricRelatedFilters()}
							</div>
						}
					</div>
					<div className="dv-filter-holder_footer">
						<Button variant="outline-secondary"
							onClick={() => resetFilter()}
							className="px-4">
							{t("Reset.label")}
						</Button>
						<Button variant="primary"
							onClick={() => applyFilter()}
							className="sbmBtn px-4">
							{t("Apply.label")}
						</Button>
					</div>
				</div>
			}
		</div>
	);
};

export default withTranslation()(DataViewFilterNew);
